import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpDown, ChevronDown, ChevronLeft, ChevronRight, Link2, MessageSquare, RefreshCw, Send } from "lucide-react";
import { Helmet } from "react-helmet";
import { withRouter } from "../../../utils/withRouter";
import api from "../../../const/axios";
import { cn } from "@/lib/utils";
import { useSelector } from "react-redux";
import StarRating from "../../Elements/StarRating";
import { isFeatureEnabled } from "../../../services/flagsService";

interface ThemeValue { id: number; text: string; answer: string; comment: string | null; sources: string[] }
interface ThemeViewData {
  theme: {
    id: number;
    name: string;
    information: string | null;
    raund: number | null;
    values: ThemeValue[];
    sources: string[];
    authors: { id: number; fullname: string }[];
  };
  packageId: number;
  packageName: string;
  phaseName: string;
  gameName: string;
}

interface DiscussionComment {
  id: number;
  text: string;
  added: string;
  user?: {
    id?: number;
    name?: string;
    surname?: string;
    photo?: string | null;
  };
}

const TextLines: React.FC<{ text: string; indent?: boolean }> = ({ text, indent }) =>
  <>
    {text.split("/-/").map((m, i) => (
      <span key={i}>
        {indent && i ? <span>&nbsp;&nbsp;</span> : null}
        {m}
        <br />
      </span>
    ))}
  </>;

interface ThemePageProps {
  params: { id: string };
}

const ThemePage: React.FC<ThemePageProps> = ({ params }) => {
  const userData = useSelector((state: any) => state.user);
  const isLoggedIn: boolean = userData?.isLoggedIn ?? false;
  const currentUser = userData?.data ?? {};
  const isVerified: boolean = currentUser?.isVerified ?? false;

  const [data, setData] = useState<ThemeViewData | null>(null);
  const [opens, setOpens] = useState<number[]>([]);
  const [notFound, setNotFound] = useState(false);

  const [copied, setCopied] = useState(false);
  const [avgRating, setAvgRating] = useState<number>(0);
  const [ratingCount, setRatingCount] = useState<number>(0);
  const [myRating, setMyRating] = useState<number | null>(null);
  const [rating, setRating] = useState(false);

  const [commentsOpen, setCommentsOpen] = useState(false);
  const [comments, setComments] = useState<DiscussionComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [commentSort, setCommentSort] = useState<"newest" | "oldest">("newest");
  const [commentText, setCommentText] = useState("");
  const [posting, setPosting] = useState(false);
  const [commentError, setCommentError] = useState<string | null>(null);
  const [commentNotice, setCommentNotice] = useState<string | null>(null);

  const commentsListRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const ratingStorageKey = useMemo(() => {
    const userId = currentUser?.id ?? "guest";
    return `rated_themes_${userId}`;
  }, [currentUser?.id]);

  const getRatingMap = (): Record<string, number> => {
    try {
      const raw = localStorage.getItem(ratingStorageKey);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };

  const setRatingMap = (next: Record<string, number>) => {
    try {
      localStorage.setItem(ratingStorageKey, JSON.stringify(next));
    } catch {
      // ignore storage errors
    }
  };

  const normalizedCommentText = commentText.trim();

  const sortedComments = useMemo(() => {
    const copied = [...comments];
    copied.sort((a, b) => {
      const av = new Date(a.added).getTime();
      const bv = new Date(b.added).getTime();
      return commentSort === "newest" ? bv - av : av - bv;
    });
    return copied;
  }, [comments, commentSort]);

  const getCommentAuthorName = (comment: DiscussionComment) =>
    `${comment.user?.name ?? ""} ${comment.user?.surname ?? ""}`.trim() || "İstifadəçi";

  const getCommentAvatarInitials = (comment: DiscussionComment) => {
    const label = getCommentAuthorName(comment);
    const parts = label.split(" ").filter(Boolean);
    if (!parts.length) return "İ";
    if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
    return `${parts[0].slice(0, 1)}${parts[1].slice(0, 1)}`.toUpperCase();
  };

  const formatCommentDate = (iso: string) => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString("az-AZ", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const fetchComments = useCallback(async (silent = false) => {
    if (!silent) {
      setCommentsLoading(true);
    }

    try {
      const res = await api.get(`themes/${params.id}/comments`);
      setComments(res.data ?? []);
      setCommentsLoaded(true);
    } finally {
      if (!silent) {
        setCommentsLoading(false);
      }
    }
  }, [params.id]);

  useEffect(() => {
    setData(null);
    setOpens([]);
    setNotFound(false);
    setComments([]);
    setCommentsLoaded(false);
    setCommentsOpen(false);
    setCommentText("");
    setCommentError(null);
    setCommentNotice(null);

    api.get(`themes/${params.id}/view`)
      .then(res => setData(res.data))
      .catch(err => {
        if (err?.response?.status === 404) setNotFound(true);
      });
  }, [params.id]);

  useEffect(() => {
    api.get(`themes/${params.id}/rating`)
      .then((res) => {
        setAvgRating(res.data?.average ?? 0);
        setRatingCount(res.data?.count ?? 0);
        if (res.data?.myRating != null) {
          setMyRating(res.data.myRating);
        } else {
          const stored = getRatingMap()[String(params.id)];
          setMyRating(stored ?? null);
        }
      })
      .catch(() => {
        const stored = getRatingMap()[String(params.id)];
        setMyRating(stored ?? null);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  useEffect(() => {
    if (!commentsOpen) return;
    if (!commentsLoaded) {
      fetchComments();
    }
    const tid = window.setTimeout(() => textareaRef.current?.focus(), 80);
    return () => window.clearTimeout(tid);
  }, [commentsOpen, commentsLoaded, fetchComments]);

  useEffect(() => {
    if (commentsOpen) return;
    setCommentError(null);
    setCommentNotice(null);
  }, [commentsOpen]);

  const toggleOpen = (idx: number) => {
    setOpens(prev => prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]);
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-20 flex items-center justify-center">
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-700 dark:text-gray-200">Mövzu tapılmadı</p>
          <Link to="/" className="mt-4 inline-block text-blue-600 hover:underline text-sm dark:text-indigo-300">Ana səhifəyə qayıt</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-20 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const th = data.theme;

  const copyShareLink = () => {
    const url = `${window.location.origin}/themes/${th.id}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const submitRating = async (value: number) => {
    if (rating) return;
    setRating(true);
    const prevRating = myRating;
    const prevAvg = avgRating;
    const prevCount = ratingCount;

    setMyRating(value);
    setRatingMap({ ...getRatingMap(), [String(th.id)]: value });

    try {
      const res = await api.post(`themes/${th.id}/rate`, { rating: value });
      setAvgRating(res.data?.average ?? prevAvg);
      setRatingCount(res.data?.count ?? prevCount);
    } catch {
      setMyRating(prevRating);
      setAvgRating(prevAvg);
      setRatingCount(prevCount);
    } finally {
      setRating(false);
    }
  };

  const submitComment = async () => {
    if (!normalizedCommentText) return;
    setPosting(true);
    setCommentError(null);
    setCommentNotice(null);

    try {
      const res = await api.post(`themes/${th.id}/comments`, { text: normalizedCommentText });
      if (res.data?.message) {
        setCommentNotice(res.data.message);
      } else {
        setCommentNotice("Şərh əlavə edildi.");
      }

      setCommentText("");
      await fetchComments(true);

      const list = commentsListRef.current;
      if (list) {
        const shouldScrollToTop = commentSort === "newest";
        requestAnimationFrame(() => {
          list.scrollTo({
            top: shouldScrollToTop ? 0 : list.scrollHeight,
            behavior: "smooth",
          });
        });
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? "Şərh göndərilərkən xəta baş verdi.";
      setCommentError(msg);
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16">
      <Helmet>
        <title>{th.name} | Mövzu arxivi — 3sual.az</title>
        <meta name="robots" content="index" />
        <meta
          name="description"
          content={`"${th.name}" mövzusu — ${data.packageName} paketindən sual və cavablar, mənbələr və müəlliflər.`}
        />
        <link rel="canonical" href={`https://3sual.az/themes/${params.id}`} />
      </Helmet>

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-4">
        {/* Breadcrumb */}
        {data.packageId > 0 && (
          <Link
            to={`/packages/${data.packageId}`}
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium dark:text-indigo-300 dark:hover:text-indigo-200"
          >
            <ChevronLeft className="w-4 h-4" />
            {data.packageName || "Paket"}
          </Link>
        )}
        {data.phaseName && (
          <p className="text-xs text-gray-400 dark:text-gray-500 -mt-2 ml-0.5 flex items-center gap-1">
            <ChevronRight className="w-3 h-3" />
            {data.phaseName}
          </p>
        )}

        {/* Theme card */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 py-5">
          <h1 className="text-base font-bold text-gray-900 dark:text-gray-50 mb-1">
            <span className="text-gray-500 dark:text-gray-400 font-semibold">Mövzu: </span>
            {th.name}
          </h1>
          {th.information && (
            <p className="text-xs text-gray-500 dark:text-gray-400 italic mb-3">{th.information}</p>
          )}

          <div className="space-y-4">
            {th.values.map((va, index) => {
              const isOpen = opens.includes(index);
              return (
                <div key={index} className="border-t border-gray-50 pt-3 first:border-0 first:pt-0">
                  <p className="text-gray-800 dark:text-gray-100 font-semibold text-sm break-words">
                    {index + 1}. <TextLines text={va.text} indent />
                  </p>

                  <button
                    onClick={() => toggleOpen(index)}
                    className="flex items-center gap-1 text-blue-500 hover:text-blue-700 transition-colors mt-1 dark:text-indigo-300 dark:hover:text-indigo-200"
                    aria-label="Cavabı göstər / gizlət"
                  >
                    <ChevronDown
                      className={cn(
                        "w-4 h-4 transition-transform duration-300",
                        isOpen && "rotate-180"
                      )}
                    />
                  </button>

                  <div
                    className={cn(
                      "overflow-hidden transition-all duration-300",
                      isOpen ? "max-h-[2000px] opacity-100 mt-1" : "max-h-0 opacity-0"
                    )}
                  >
                    <div className="space-y-1 text-sm">
                      <p className="text-gray-700 dark:text-gray-200 font-medium">
                        <span className="font-bold text-gray-900 dark:text-gray-50">Cavab: </span>
                        {va.answer}
                      </p>
                      {va.comment?.trim() && (
                        <p className="text-gray-700 dark:text-gray-200">
                          <span className="font-bold text-gray-900 dark:text-gray-50">Şərh: </span>
                          {va.comment}
                        </p>
                      )}
                      {va.sources.length > 0 && (
                        <p className="text-gray-700 dark:text-gray-200 break-all">
                          <span className="font-bold text-gray-900 dark:text-gray-50">Mənbə: </span>
                          {va.sources.map((src, i) => (
                            <React.Fragment key={i}>
                              {i > 0 && <><br />&nbsp;&nbsp;&nbsp;&nbsp;</>}
                              {va.sources.length > 1 ? `${i + 1}. ${src}` : src}
                            </React.Fragment>
                          ))}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {isFeatureEnabled('QUESTION_ENGAGEMENT_BAR') && (
          <div className="mt-3 flex items-center gap-2 text-xs">
            <StarRating
              average={avgRating}
              count={ratingCount}
              myRating={myRating}
              onRate={submitRating}
              disabled={rating}
              loading={rating}
              canRate={isLoggedIn && isVerified}
            />
            {((isLoggedIn && isVerified) || ratingCount !== 0) && <span className="text-gray-300">•</span>}
            <button
              onClick={() => setCommentsOpen((prev) => !prev)}
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
              aria-label="Şərhləri aç"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              Şərhlər ({comments.length})
            </button>
            <span className="text-gray-300">•</span>
            <button
              onClick={copyShareLink}
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
              aria-label="Linki kopyala"
            >
              <Link2 className="h-3.5 w-3.5" />
              {copied ? "Kopyalandı!" : "Paylaş"}
            </button>
          </div>
          )}

          <div
            className={cn(
              // Side padding (offset by the negative margin) leaves room for the textarea focus ring,
              // which overflow-hidden would otherwise clip.
              "-mx-1 px-1 overflow-hidden transition-all duration-300",
              commentsOpen ? "max-h-[900px] opacity-100 mt-3" : "max-h-0 opacity-0"
            )}
          >
            <div className="rounded-xl bg-white dark:bg-gray-900">
              <div className="flex items-start justify-between border-b border-gray-100 dark:border-gray-800 py-3 gap-4">
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Mövzu üzrə müzakirə</h3>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate">{th.name}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => fetchComments()}
                    disabled={commentsLoading}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-200 dark:border-gray-700 px-2 py-1 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                    title="Yenilə"
                  >
                    <RefreshCw className={cn("h-3.5 w-3.5", commentsLoading && "animate-spin")} />
                    Yenilə
                  </button>
                  <button
                    onClick={() => setCommentSort((prev) => (prev === "newest" ? "oldest" : "newest"))}
                    className="inline-flex items-center gap-1 rounded-md border border-gray-200 dark:border-gray-700 px-2 py-1 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                    title="Sıralamanı dəyiş"
                  >
                    <ArrowUpDown className="h-3.5 w-3.5" />
                    {commentSort === "newest" ? "Ən yeni" : "Ən köhnə"}
                  </button>
                </div>
              </div>

              <div ref={commentsListRef} className="py-3 overflow-y-auto max-h-64">
                {commentsLoading ? (
                  <ul className="space-y-3">
                    {Array.from({ length: 3 }).map((_, idx) => (
                      <li key={idx} className="rounded-xl border border-gray-100 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/40 p-3 animate-pulse">
                        <div className="h-3 w-40 bg-gray-200 dark:bg-gray-700 rounded" />
                        <div className="mt-2 h-3 w-full bg-gray-200 dark:bg-gray-700 rounded" />
                        <div className="mt-1 h-3 w-3/4 bg-gray-200 dark:bg-gray-700 rounded" />
                      </li>
                    ))}
                  </ul>
                ) : sortedComments.length > 0 ? (
                  <ul className="space-y-3 mb-2">
                    {sortedComments.map((c) => {
                      const isMine = Boolean(currentUser?.id) && c.user?.id === currentUser?.id;
                      return (
                        <li
                          key={c.id}
                          className={cn(
                            "rounded-xl border p-3 text-sm",
                            isMine ? "border-blue-200 bg-blue-50/60 dark:border-indigo-900 dark:bg-indigo-950/40" : "border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700 text-[11px] font-semibold text-gray-700 dark:text-gray-200">
                              {getCommentAvatarInitials(c)}
                            </div>
                            <span className="font-medium text-gray-800 dark:text-gray-100">{getCommentAuthorName(c)}</span>
                            {isMine && (
                              <span className="rounded-full bg-blue-100 dark:bg-indigo-900/60 px-2 py-0.5 text-[10px] font-medium text-blue-700 dark:text-indigo-300">
                                Siz
                              </span>
                            )}
                            <span className="text-gray-400 dark:text-gray-500 text-xs">{formatCommentDate(c.added)}</span>
                          </div>
                          <p className="text-gray-700 dark:text-gray-200 mt-2 break-words whitespace-pre-wrap">{c.text}</p>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 py-8 text-center">
                    <MessageSquare className="mx-auto h-7 w-7 text-gray-300" />
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Hələ şərh yoxdur.</p>
                  </div>
                )}
              </div>

              <div className="border-t border-gray-100 dark:border-gray-800 py-3 bg-white dark:bg-gray-900">
                {isLoggedIn && isVerified ? (
                  <div className="flex flex-col gap-1.5">
                    <textarea
                      ref={textareaRef}
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      onKeyDown={(e) => {
                        if ((e.ctrlKey || e.metaKey) && e.key === "Enter" && !posting && normalizedCommentText) {
                          e.preventDefault();
                          submitComment();
                        }
                      }}
                      placeholder="Şərhinizi yazın..."
                      rows={3}
                      maxLength={1000}
                      className="w-full text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                    />
                    <div className="flex items-center justify-between text-[11px] text-gray-400 dark:text-gray-500">
                      <span>Göndərmək üçün Ctrl+Enter istifadə edin</span>
                      <span>{commentText.length}/1000</span>
                    </div>
                    {commentError && <p className="text-xs text-red-500">{commentError}</p>}
                    {commentNotice && <p className="text-xs text-emerald-600">{commentNotice}</p>}
                    <button
                      onClick={submitComment}
                      disabled={posting || !normalizedCommentText}
                      className="self-end inline-flex items-center gap-1.5 text-xs bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white px-3 py-1.5 rounded-lg transition-colors"
                    >
                      <Send className="h-3.5 w-3.5" />
                      {posting ? "Göndərilir..." : "Göndər"}
                    </button>
                  </div>
                ) : isLoggedIn && !isVerified ? (
                  <p className="text-xs text-amber-600">Şərh yazmaq üçün hesabınızın təsdiqlənməsi lazımdır.</p>
                ) : (
                  <p className="text-xs text-gray-400 dark:text-gray-500">Şərh yazmaq üçün daxil olun.</p>
                )}
              </div>
            </div>
          </div>

          {/* Theme-level sources */}
          {th.sources.length > 0 && (
            <div className="mt-4 border-t border-gray-100 dark:border-gray-800 pt-3 text-sm text-gray-700 dark:text-gray-200 break-all">
              <span className="font-bold text-gray-900 dark:text-gray-50">Mənbə: </span>
              {th.sources.map((src, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <><br />&nbsp;&nbsp;&nbsp;&nbsp;</>}
                  {th.sources.length > 1 ? `${i + 1}. ${src}` : src}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Theme-level authors */}
          {th.authors.length > 0 && (
            <p className="text-sm text-gray-700 dark:text-gray-200 mt-2">
              <span className="font-bold text-gray-900 dark:text-gray-50">
                {th.authors.length > 1 ? "Müəlliflər:" : "Müəllif:"}
              </span>{" "}
              {th.authors.map((a, i) => (
                <React.Fragment key={i}>
                  {i > 0 ? ", " : ""}
                  {a.fullname}
                </React.Fragment>
              ))}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default withRouter(ThemePage);
